import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ReactComponent as LocationIcon } from '../../assets/svg/location_icon.svg';
import { ReactComponent as SeasonIcon } from '../../assets/svg/season_icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/svg/filter_icon_rounded.svg';
import { ReactComponent as CloseIconBlack } from '../../assets/svg/close-black.svg';
import { ReactComponent as EditDarkIcon } from '../../assets/svg/edit_dark.svg';
import { useStyles } from './styles';
import { Modal } from '@material-ui/core';
import ReactGoogleAutocomplete from 'react-google-autocomplete';
import './style.scss';
import useRequestParameters from './hooks/useRequestParameters';

const SEASONS = [
    { value: 'Summer', isSelected: false, isApplied: false },
    { value: 'Autumn', isSelected: false, isApplied: false },
    { value: 'Spring', isSelected: false, isApplied: false },
    { value: 'Winter', isSelected: false, isApplied: false },
];

const IP_URL = 'https://us.freeipapi.com/api/json';

const Chip = ({ Icon, values, index }) => {
    const classes = useStyles();
    return (
        <div className={`${classes.chip} ${classes.cursorPointer}`}>
            <Icon className={classes.chipIcon} />
            <div className={classes.chipText}>
                {values.map((item, index) => (
                    <span key={index}>
                        {item}
                        {values[index + 1] ? ', ' : ''}
                    </span>
                ))}
            </div>
        </div>
    );
};

const ChipOption = ({ option, index, onChipClick }) => {
    const classes = useStyles();
    return (
        <div
            className={`${classes.sectionChip} ${classes.cursorPointer}`}
            onClick={onChipClick}
            style={{ background: option.isSelected ? '#F5DFCE' : '#ffffff' }}
        >
            <div className={classes.sectionChipText}>{option.value}</div>
        </div>
    );
};

const SectionTitle = ({ title }) => {
    const classes = useStyles();
    return <div className={classes.sectionTitle}>{title}</div>;
};

const getSeason = (latitude) => {
    const month = new Date().getMonth();
    const northern = latitude >= 0;

    if (month === 11 || month === 0 || month === 1) {
        return northern ? 'Winter' : 'Summer';
    } else if (month >= 2 && month <= 4) {
        return northern ? 'Spring' : 'Autumn';
    } else if (month >= 5 && month <= 7) {
        return northern ? 'Summer' : 'Winter';
    } else {
        return northern ? 'Autumn' : 'Spring';
    }
};

export default function AmiraFilter() {
    const classes = useStyles();
    const placeFieldRef = useRef<HTMLInputElement | null>(null);
    const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);
    const [seasons, setSeasons] = useState(SEASONS);
    const [locations, setLocations] = useState<any>([]);
    const [prevFilters, setPrevFilters] = useState<any>({});
    const [isDisplayLocationField, setIsDisplayLocationField] = useState(false);
    const { setRequestParameters } = useRequestParameters();

    const getSelectedValues = (stateValue, defaultName) => {
        let selectedValues: any = [];
        if (stateValue.length) {
            selectedValues = stateValue.filter((item) => item.value && item.isApplied).map((item) => item.value);
        }
        if (selectedValues.length) {
            return selectedValues;
        } else {
            return [defaultName];
        }
    };
    const filters = useMemo(
        () => [
            {
                icon: LocationIcon,
                values: getSelectedValues(locations, 'Location'),
                stateName: 'locations',
                displayName: 'Location',
            },
            {
                icon: SeasonIcon,
                values: getSelectedValues(seasons, 'Season'),
                stateName: 'seasons',
                displayName: 'Season',
            },
        ],
        [locations, seasons],
    );
    const appliedLocation = useMemo(() => {
        return locations.find((item) => item.isApplied) ?? null;
    }, [locations]);
    const appliedSeason = useMemo(() => {
        return seasons.find((item) => item.isApplied) ?? null;
    }, [seasons]);

    const applyPrevFilters = () => {
        setLocations(prevFilters.locations);
        setSeasons(prevFilters.seasons);
    };

    const closeFilterPopup = (isCloseClicked) => {
        setIsFilterPopupOpen(false);
        setIsDisplayLocationField(false);
        if (isCloseClicked) {
            applyPrevFilters();
        }
    };
    const openFilterPopup = () => {
        setIsFilterPopupOpen(true);
        setPrevFilters({
            seasons,
            locations,
        });
    };
    const updateFilterPayload = useCallback(
        ({ season, location }) => {
            let requestParameterString = '';
            if (location) {
                requestParameterString = `Location: ${location.value}`;
            }
            if (season) {
                requestParameterString = requestParameterString
                    ? `${requestParameterString}, Season: ${season.value}`
                    : `Season: ${season.value}`;
            }
            setRequestParameters(requestParameterString);
        },
        [setRequestParameters],
    );
    const handleApplyFilters = () => {
        setSeasons((prevData) =>
            prevData.map((item) => {
                return { ...item, isApplied: item.isSelected };
            }),
        );
        setLocations((prevData) =>
            prevData.map((item) => {
                return { ...item, isApplied: item.isSelected };
            }),
        );
        closeFilterPopup(false);
    };

    const handleChipClick = (options, option, func) => {
        const updatedOptions = options.map((item) => {
            if (option.value === item.value) {
                return { ...item, isSelected: !option.isSelected, isApplied: false };
            }
            return { ...item, isSelected: false, isApplied: false };
        });
        func(updatedOptions);
    };

    const handleDisplayLocationField = () => {
        setIsDisplayLocationField(true);
        if (placeFieldRef?.current) {
            placeFieldRef.current.focus();
        }
    };

    const handlePlaceSelected = (place) => {
        if (placeFieldRef?.current) {
            placeFieldRef.current.value = '';
        }
        setIsDisplayLocationField(false);
        const cityComponent = place.address_components?.find(
            (component) =>
                component.types.includes('locality') || component.types.includes('administrative_area_level_2'),
        );
        const city = cityComponent?.long_name || '';
        setLocations([{ value: city, isSelected: true, isApplied: false }]);
    };

    const getSeasonsList = useCallback(
        (currentSeason) => {
            return seasons.map((item) => {
                if (item.value === currentSeason) {
                    return { ...item, isSelected: true, isApplied: true };
                }
                return item;
            });
        },
        [seasons],
    );

    const getCurrentLocationFromIp = useCallback(() => {
        fetch(IP_URL, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${process.env.REACT_APP_IP_API_KEY}`,
                'Content-Type': 'application/json',
            },
        })
            .then(async (res) => {
                if (!res?.ok) {
                    const errorText = await res?.text();
                    throw new Error(`HTTP ${res?.status} - ${errorText}`);
                }
                return res.json(); // Only try to parse if status is OK
            })
            .then((data) => {
                setLocations([{ value: data.cityName, isSelected: true, isApplied: true }]);
                const currentSeason = getSeason(data?.latitude);
                setSeasons(getSeasonsList(currentSeason));
            })
            .catch((error) => {
                console.error('Fetch error:', error?.message);
            });
    }, [getSeasonsList]);

    useEffect(() => {
        updateFilterPayload({
            season: appliedSeason,
            location: appliedLocation,
        });
    }, [appliedSeason, appliedLocation, updateFilterPayload]);

    useEffect(() => {
        if (!locations.length) {
            getCurrentLocationFromIp();
        }
    }, [locations.length, getCurrentLocationFromIp]);

    return (
        <>
            <div className={classes.filterContainer}>
                <div className={classes.chipContainer} onClick={() => openFilterPopup()}>
                    {filters.map((item, index) => (
                        <Chip key={index} index={index} Icon={item.icon} values={item.values} />
                    ))}
                </div>
                <div>
                    <FilterIcon
                        className={`${classes.filterIcon} ${classes.cursorPointer}`}
                        onClick={() => openFilterPopup()}
                    />
                </div>
            </div>
            <Modal open={isFilterPopupOpen} onClose={() => closeFilterPopup(true)} style={{ margin: 0 }}>
                <div className={classes.filterModalContainer}>
                    <div className={classes.modalTitleContainer}>
                        <div className={classes.w50}></div>
                        <div className={classes.modalTitle}>Style Context</div>
                        <div onClick={() => closeFilterPopup(true)} className={`${classes.closeButton} ${classes.w50}`}>
                            <CloseIconBlack />
                        </div>
                    </div>
                    <div className={classes.modalBodyContainer}>
                        <div className={classes.section}>
                            <SectionTitle title={'Location'} />
                            <div className={classes.sectionContainer}>
                                {locations.map((option, index) => {
                                    return (
                                        <ChipOption
                                            option={option}
                                            key={index}
                                            index={index}
                                            onChipClick={() => handleChipClick(locations, option, setLocations)}
                                        />
                                    );
                                })}
                                <EditDarkIcon className={classes.addIcon} onClick={handleDisplayLocationField} />
                            </div>
                            <div
                                className={classes.mt15}
                                style={{
                                    visibility: isDisplayLocationField ? 'visible' : 'hidden',
                                    height: isDisplayLocationField ? 'auto' : 0,
                                }}
                            >
                                <ReactGoogleAutocomplete
                                    apiKey={process.env.REACT_APP_FIREBASE_API_KEY}
                                    onPlaceSelected={(place) => handlePlaceSelected(place)}
                                    className={classes.locationInput}
                                    defaultValue={''}
                                    ref={placeFieldRef}
                                />
                            </div>
                        </div>
                        <div className={classes.section}>
                            <SectionTitle title={'Season'} />
                            <div className={classes.sectionContainer}>
                                {seasons.map((option, index) => {
                                    return (
                                        <ChipOption
                                            option={option}
                                            key={index}
                                            index={index}
                                            onChipClick={() => handleChipClick(seasons, option, setSeasons)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        <div className={classes.section}>
                            <div
                                className={`${classes.applyButton} ${classes.cursorPointer}`}
                                onClick={handleApplyFilters}
                            >
                                APPLY
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}
